import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import { openGlobalPopup, closeGlobalPopup } from "../globalPopupSlice";
import Icon from "../../../components/Icon/Icon";

// Constants for storage
// We switched to sessionStorage, so we use a key indicating session visibility
const POPUP_SESSION_KEY = "theme_popup_seen_session";

const GlobalPopup: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, imageUrl, linkUrl, linkTarget } = useAppSelector(
    (state) => state.globalPopup,
  );
  const portalRoot = document.body;
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 1. Get popup configuration from localized WordPress variables
    const popupImage = window.commonVariables?.popupImage;
    const popupLink = window.commonVariables?.popupLink;

    if (!popupImage) return;

    // 2. Check SessionStorage
    // sessionStorage persists while the tab is open (including reloads),
    // but clears when the tab/browser is closed. This satisfies "once per session".
    const hasSeenInSession = sessionStorage.getItem(POPUP_SESSION_KEY);

    // 3. Dispatch open action if user hasn't seen it in this session
    if (!hasSeenInSession) {
      dispatch(
        openGlobalPopup({
          imageUrl: popupImage,
          linkUrl: popupLink?.url,
          linkTarget: popupLink?.target,
        }),
      );
    }
  }, [dispatch]);

  /**
   * Closes the popup and records the event in sessionStorage.
   */
  const handleClose = () => {
    // Mark as seen for this specific tab session
    sessionStorage.setItem(POPUP_SESSION_KEY, "true");
    dispatch(closeGlobalPopup());
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };

    if (isOpen) {
      document.body.classList.add("global-popup-open");
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.classList.remove("global-popup-open");
    }

    return () => {
      document.body.classList.remove("global-popup-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Click outside to close logic
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  if (!isOpen || !portalRoot || !imageUrl) {
    return null;
  }

  const imageElement = <img src={imageUrl} alt="Announcement" />;

  return ReactDOM.createPortal(
    <div
      className={`global-popup-wrapper ${isOpen ? "is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="global-popup__overlay" onClick={handleOverlayClick} />
      <div
        ref={contentRef}
        className="global-popup__content"
        role="dialog"
        aria-modal="true"
      >
        <button
          onClick={handleClose}
          className="global-popup__close-btn"
          aria-label="Close popup"
        >
          <Icon iconName="x-mark" className="icon--stroke-thickest" />
        </button>

        {/* Render image inside a link if a URL is provided in ACF Settings */}
        {linkUrl ? (
          <a
            href={linkUrl}
            target={linkTarget || "_self"}
            rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
            onClick={() => {
              // Also trigger the "seen" logic when the user clicks the link
              handleClose();
            }}
          >
            {imageElement}
          </a>
        ) : (
          imageElement
        )}
      </div>
    </div>,
    portalRoot,
  );
};

export default GlobalPopup;
